/**
 * lib/invoicePdf.ts
 * ------------------------------------------------------------------
 * A4 single-invoice PDF in the finance house style (mirrors
 * statementPdf.ts / payslipPdf.ts: jsPDF, navy letterhead, SCHOOL
 * config, fmtPula). Used by the joining-invoice flow (Admit wizard)
 * and ManualInvoice so every emailed / downloaded invoice looks the
 * same.
 *
 * The whole point of the document is the PAYMENT REFERENCE block: the
 * pupil's name, large and boxed, with a plain instruction to quote it
 * when paying by EFT / online banking. (It was the invoice number until
 * 10 Oct 2026; the school matches payments by the pupil's name.)
 * `payInstructions` lets the bursar drop in real bank details later
 * without touching this file.
 *
 * buildInvoicePdf(inv, opt).output('blob') → upload to Storage / email.
 * ---------------------------------------------------------------- */
import { jsPDF } from 'jspdf';
import { SCHOOL } from '@/lib/school.config';
import { termLabel } from '@/lib/school.config';
import { termLabelForCohort } from '@/lib/cohort';
import { DISCOUNT_KIND_LABELS, feeAfterDiscounts, fmtPula, FEE_CATEGORY_LABELS, type Invoice, type Standing } from '@/lib/financeCore';
import { invoiceFooterLines } from '@/lib/standingText';

export interface InvoicePdfOptions {
  logoDataUrl?: string | null;
  /** Overrides the default "quote the pupil's name when paying" instruction. */
  payInstructions?: string;
  /**
   * THE PUPIL'S WHOLE ACCOUNT, so the invoice can say where it sits.
   *
   * Without it this document states a term's fee and nothing else, which is
   * how a family the school was already holding P24,000 for came to be sent
   * a P22,000 bill that read as a demand. The invoice still states the fee
   * IN FULL - netting the credit into it would understate fee income
   * permanently - so the account is printed beside the total, not inside it.
   *
   * Optional: an invoice for a pupil with no prior account (a joining
   * invoice from the Admit wizard) has nothing to say here and says nothing.
   */
  standing?: Standing & { documentCount?: number };
}

const DEFAULT_PAY_INSTRUCTIONS =
  'Payment is by bank transfer (EFT), online banking, or card. When paying, please quote the ' +
  'pupil\'s name shown above as your payment reference so we can match your payment to the ' +
  'right child. Contact the bursar for the school bank account details.';

export function buildInvoicePdf(inv: Invoice, opt: InvoicePdfOptions = {}): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const MX = 16; const right = 210 - MX;
  const NAVY: [number, number, number] = [27, 58, 107];
  const INK: [number, number, number] = [31, 41, 55];
  const MUTED: [number, number, number] = [107, 114, 128];
  const RULE: [number, number, number] = [209, 213, 219];
  const GREEN: [number, number, number] = [5, 150, 105];
  const setT = (c: [number, number, number]) => pdf.setTextColor(c[0], c[1], c[2]);
  const setD = (c: [number, number, number]) => pdf.setDrawColor(c[0], c[1], c[2]);
  const setF = (c: [number, number, number]) => pdf.setFillColor(c[0], c[1], c[2]);
  let y = 16;

  // ---- letterhead ----
  if (opt.logoDataUrl) {
    try { pdf.addImage(opt.logoDataUrl, 'PNG', MX, y - 2, 18, 18); } catch { /* ignore */ }
  }
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14);
  pdf.text(SCHOOL.name, MX + 22, y + 4);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
  pdf.text(`${SCHOOL.contact.phonePrimary}  ·  ${SCHOOL.contact.email}  ·  ${SCHOOL.contact.website}`, MX + 22, y + 9);
  y += 22;
  setD(RULE); pdf.setLineWidth(0.4); pdf.line(MX, y, right, y); y += 8;

  // ---- title + invoice meta ----
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15);
  pdf.text('INVOICE', MX, y);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); setT(MUTED);
  pdf.text(`No. ${inv.invoiceNo}`, right, y - 3.5, { align: 'right' });
  pdf.text(`Issued ${(inv.issuedAt || '').slice(0, 10)}`, right, y + 0.5, { align: 'right' });
  pdf.text(`Due ${(inv.dueDate || '').slice(0, 10)}`, right, y + 4.5, { align: 'right' });
  y += 8;

  // ---- bill-to ----
  pdf.setFontSize(7.5); setT(MUTED); pdf.text('FOR', MX, y);
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10); setT(INK);
  pdf.text(inv.studentName || inv.studentId, MX, y + 4.5);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); setT(MUTED);
  pdf.text(`${termLabelForCohort(termLabel(inv.term), inv.cohort)} ${inv.year}`, MX, y + 9);
  y += 16;

  // ---- line items ----
  const cell = (txt: string, x: number, align: 'left' | 'right' = 'left') => pdf.text(txt, x, y, { align });
  setF(NAVY); pdf.rect(MX, y - 4.5, right - MX, 7, 'F');
  pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8.5);
  cell('DESCRIPTION', MX + 2); cell('CATEGORY', right - 52, 'left'); cell('AMOUNT (BWP)', right - 2, 'right');
  y += 6;
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
  for (const l of inv.lines) {
    if (y > 250) { setT(MUTED); pdf.setFontSize(8); pdf.text('… continued', MX + 2, y); y += 5; break; }
    setT(INK); pdf.text(pdf.splitTextToSize(l.label, 105)[0] as string, MX + 2, y);
    setT(MUTED); pdf.setFontSize(8); pdf.text(FEE_CATEGORY_LABELS[l.category] ?? l.category, right - 52, y);
    setT(INK); pdf.setFontSize(9); pdf.text(fmtPula(l.amount), right - 2, y, { align: 'right' });
    y += 6;
  }
  y += 1;
  setD(RULE); pdf.setLineWidth(0.3); pdf.line(MX, y, right, y); y += 6;

  // ---- subtotal + discounts ----
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
  setT(MUTED); pdf.text('Subtotal', right - 42, y, { align: 'right' });
  setT(INK); pdf.text(fmtPula(inv.subtotal), right - 2, y, { align: 'right' }); y += 5.5;
  for (const d of inv.discounts) {
    setT(MUTED); pdf.text(pdf.splitTextToSize(d.label, 90)[0] as string, MX + 2, y);
    setT(GREEN); pdf.text(`− ${fmtPula(d.amount)}`, right - 2, y, { align: 'right' }); y += 5.5;
  }
  // Issued against the invoice afterwards, so the fee due below is the reduced one.
  for (const d of inv.issuedDiscounts ?? []) {
    setT(MUTED); pdf.text(`${DISCOUNT_KIND_LABELS[d.kind] ?? 'Discount'} (${d.discountNo})`, MX + 2, y);
    setT(GREEN); pdf.text(`− ${fmtPula(d.amount)}`, right - 2, y, { align: 'right' }); y += 5.5;
  }
  y += 1;

  // ---- total due box ----
  setF([240, 244, 250]); setD(NAVY); pdf.setLineWidth(0.5);
  pdf.rect(MX, y, right - MX, 13, 'FD');
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11);
  pdf.text('TOTAL DUE', MX + 3, y + 8.4);
  pdf.setFontSize(13);
  pdf.text(fmtPula(feeAfterDiscounts(inv)), right - 3, y + 8.6, { align: 'right' });
  y += 20;

  /* ---- where this invoice sits on the account ----
   *
   * A SNAPSHOT, AND SAID TO BE ONE. This PDF is stored and re-opened months
   * later; a balance printed without a date would be read as today's. The
   * as-at line is what makes a stale figure obviously a stale figure rather
   * than a wrong one. */
  if (opt.standing) {
    const f = invoiceFooterLines(feeAfterDiscounts(inv), inv.balance, opt.standing);
    const asAt = (opt.standing.asAt || '').slice(0, 10);
    const noteLines = f.note
      ? (pdf.splitTextToSize(f.note, right - MX - 6) as string[])
      : [];
    const boxH = 15 + noteLines.length * 4;

    setF([250, 250, 252]); setD(RULE); pdf.setLineWidth(0.3);
    pdf.rect(MX, y, right - MX, boxH, 'FD');
    setT(MUTED); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7.5);
    pdf.text(
      asAt ? `YOUR ACCOUNT AS AT ${asAt}` : 'YOUR ACCOUNT',
      MX + 3, y + 5,
    );
    setT(INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10.5);
    pdf.text(f.account, MX + 3, y + 11);
    let ny = y + 15;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8);
    setT(MUTED);
    for (const line of noteLines) { pdf.text(line, MX + 3, ny); ny += 4; }
    y += boxH + 6;
  }

  // ---- instalment schedule (when the invoice is split) ----
  if (inv.instalments && inv.instalments.length > 1) {
    setT(MUTED); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8);
    pdf.text('PAYMENT SCHEDULE', MX, y); y += 4.5;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5);
    for (const ins of inv.instalments) {
      setT(INK); pdf.text(`${ins.label} — due ${(ins.dueDate || '').slice(0, 10)}`, MX + 2, y);
      pdf.text(fmtPula(ins.amount), right - 2, y, { align: 'right' });
      y += 5;
    }
    y += 4;
  }

  // ---- payment reference block (the copy-friendly bit) ----
  setT(MUTED); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8);
  pdf.text('PAYMENT REFERENCE — QUOTE WHEN PAYING', MX, y); y += 3;
  setD(NAVY); pdf.setLineWidth(0.5); setF([255, 255, 255]);
  pdf.rect(MX, y, right - MX, 11, 'FD');
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15);
  pdf.text(String(inv.studentName || inv.invoiceNo), MX + 4, y + 7.6);
  y += 17;

  // ---- instructions ----
  setT(MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8);
  pdf.text(
    pdf.splitTextToSize(opt.payInstructions ?? DEFAULT_PAY_INSTRUCTIONS, right - MX) as string[],
    MX, y,
  );

  return pdf;
}
